import { log } from '@ms-ows/logging'

import { getTargetElementInfoWithFallback } from '../../dom'
import {
  ActionType,
  HostRenderSurfaceParams,
  MultiSurfaceParams,
  SurfaceParams,
  TargetElementInfo,
} from '../../models'
import { createSurfaceParamsByIndex } from '../../surface-params'
import { performActionBeaconCallback } from '../beacon'
import { logElapsedEvent } from '../telemetry'

import { handleClickAction } from './actions'
import { renderMultipleSurfaces } from './multiple'
import { getAndProcessTargetElement, renderInternal } from './single'

// tslint:disable-next-line: max-func-body-length
const convertMultiSurfaceParamsToArray = (
  surfaceParams: MultiSurfaceParams,
  onClosed: (button: ActionType) => void,
  getDismissActions: () => (() => void)[] | undefined,
  getOnUnsupportedSurfaceTypeCallback: (
    convertedSurfaceParams: SurfaceParams,
    closeCallback: (button: ActionType) => void
  ) => () => void,
  launchTime: number,
  surfaceId: string | undefined
): HostRenderSurfaceParams[] => {
  const hostCloseAction = (
    curIndex: number,
    closeRemaining = false,
    curParams?: HostRenderSurfaceParams
  ) => {
    const end = closeRemaining ? undefined : curIndex + 1
    const dismissActions = getDismissActions()
    try {
      if (dismissActions && curIndex >= 0 && curIndex < dismissActions.length) {
        dismissActions
          .slice(curIndex, end)
          .reverse() // Often dismissing current triggers next, so dismiss starting with last
          .forEach(action => {
            action && action()
          })
      }

      if (closeRemaining && curParams) {
        handleClickAction(
          log.reserve(0x217d2848 /* tag_75s7i */),
          'close',
          undefined,
          curParams.surfaceType,
          curParams.closeButtonAction,
          curParams.closeButtonActionParams
        )
      }
    } catch (error) {
      log.error(
        0x2361a7d7 /* tag_9y05x */,
        `failed to dismiss surface ${curIndex + 1} of ${
          surfaceParams.itemCount
        }${closeRemaining ? 'plus all remaining surfaces' : undefined}`,
        error as unknown
      )
    }
  }

  // convert surfaceParams to SurfaceParams[]
  const surfaceParamsArray: HostRenderSurfaceParams[] = []
  for (
    let surfaceIndex = 1;
    surfaceIndex <= surfaceParams.itemCount;
    surfaceIndex++
  ) {
    const curParams = createSurfaceParamsByIndex(surfaceIndex, surfaceParams)
    const targetElementInfo = getTargetElementInfoWithFallback(
      curParams.anchorElementSelectors
    )
    const arrayIndex = surfaceIndex - 1 // surface index is 1-based

    const buttonAction = (button: ActionType) => {
      if (button === 'error' && arrayIndex < 1) {
        onClosed && onClosed(button)

        return
      }

      logElapsedEvent(
        log.reserve(0x2361a7d8 /* tag_9y05y */),
        'SurfaceDismissed',
        button,
        arrayIndex + 1, // surface index is 1-based
        launchTime,
        surfaceId,
        surfaceParams.itemCount
      )

      performActionBeaconCallback(button, surfaceParams)

      switch (button) {
        case 'primary':
          handleClickAction(
            log.reserve(0x1f1442a2 /* tag_5fek8 */),
            'primary',
            targetElementInfo?.selector,
            curParams.surfaceType,
            curParams.primaryButtonAction,
            curParams.primaryButtonActionParams
          )
          break
        case 'secondary':
          handleClickAction(
            log.reserve(0x1f1442a3 /* tag_5fek9 */),
            'secondary',
            targetElementInfo?.selector,
            curParams.surfaceType,
            curParams.secondaryButtonAction,
            curParams.secondaryButtonActionParams
          )
          break
        case 'close':
          handleClickAction(
            log.reserve(0x1f1442c0 /* tag_5fela */),
            'close',
            targetElementInfo?.selector,
            curParams.surfaceType,
            curParams.closeButtonAction,
            curParams.closeButtonActionParams
          )
          break
        default:
        // ignore
      }

      if (
        arrayIndex < surfaceParams.itemCount - 1 &&
        (button === 'primary' || button === 'error')
      ) {
        hostCloseAction(arrayIndex, false, surfaceParamsArray[arrayIndex])
      } else {
        // last surface element or auto-dismiss or close or secondary or
        // upon clicking a non-primary button, the rendering of sequenced teaching ui will be stopped
        onClosed && onClosed(button)
        hostCloseAction(arrayIndex, true, surfaceParamsArray[arrayIndex])
      }
    } // buttonAction

    surfaceParamsArray.push({
      ...curParams,
      ...targetElementInfo,
      onClosed: () => buttonAction('close'),
      onUnsupportedSurfaceType: getOnUnsupportedSurfaceTypeCallback(
        curParams,
        buttonAction
      ),
      primaryButtonAction:
        curParams.primaryButtonText && (() => buttonAction('primary')),
      secondaryButtonAction:
        curParams.secondaryButtonText && (() => buttonAction('secondary')),
    })
  }

  return surfaceParamsArray
}

export const renderHostMultiple = async (
  launchTime: number,
  onClosed: (button: ActionType) => void,
  onUpdated: (() => void) | undefined,
  renderCallback: (
    surfaceParams: HostRenderSurfaceParams[]
  ) => Promise<(() => void)[]>,
  renderTargetId: string,
  surfaceId: string | undefined,
  surfaceLaunchedEvent: (() => void) | undefined,
  surfaceParams: MultiSurfaceParams
) => {
  let dismissActions: (() => void)[] | undefined

  const getOnUnsupportedSurfaceTypeCallback =
    (
      convertedSurfaceParams: SurfaceParams,
      closeCallback: (button: ActionType) => void
    ) =>
    async () => {
      const targetInfo = getAndProcessTargetElement(
        convertedSurfaceParams,
        surfaceLaunchedEvent,
        closeCallback
      )

      if (!targetInfo) {
        return
      }

      await renderInternal(
        convertedSurfaceParams,
        targetInfo,
        surfaceLaunchedEvent,
        onUpdated,
        closeCallback
      )
    }

  const surfaceParamsArray: HostRenderSurfaceParams[] =
    convertMultiSurfaceParamsToArray(
      surfaceParams,
      onClosed,
      () => dismissActions,
      getOnUnsupportedSurfaceTypeCallback,
      launchTime,
      surfaceId
    )

  // log isControl or isAA
  const targetElementInfo = getAndProcessTargetElement(
    surfaceParamsArray[0],
    surfaceLaunchedEvent,
    onClosed
  )
  if (!targetElementInfo) {
    return
  }

  try {
    dismissActions = await renderCallback(surfaceParamsArray)
    log.info(
      0x2361a7d9 /* tag_9y05z */,
      'host render succeeded (multi-surface)'
    )
    surfaceLaunchedEvent?.()
  } catch (error) {
    log.error(
      0x2361a7da /* tag_9y050 */,
      'error with host rendering (multi-surface), falling back to internal renderer',
      error
    )

    await renderMultipleSurfaces(
      surfaceParams,
      renderCallback,
      1,
      renderTargetId,
      surfaceLaunchedEvent,
      onUpdated,
      onUpdated,
      onClosed
    )
  }
}

export const renderHostSingle = async (
  onClosed: ((button: ActionType) => void) | undefined,
  onUpdated: (() => void) | undefined,
  renderCallback: (
    surfaceParams: HostRenderSurfaceParams[]
  ) => Promise<(() => void)[]>,
  surfaceLaunchedEvent: (() => void) | undefined,
  surfaceParams: SurfaceParams,
  targetElementInfo: TargetElementInfo,
  launchTime: number,
  surfaceId: string | undefined
) => {
  let dismissActions: (() => void)[] | undefined

  const closeSurface = (button: ActionType) => {
    onClosed?.(button)
    dismissActions?.[0]?.()
  }

  try {
    dismissActions = await renderCallback([
      {
        ...surfaceParams,
        ...targetElementInfo,
        onClosed: reason => {
          closeSurface(reason)
        },
        onFullRenderCompleted: isCoachMarkBubble => {
          if (!isCoachMarkBubble) {
            logElapsedEvent(
              log.reserve(0x1f604047 /* tag_5yebh */),
              'SurfaceFullRenderCompleted',
              'full render complete',
              0, // surface index is 1-based
              launchTime,
              surfaceId
            )
          }
        },
        onUnsupportedSurfaceType: () =>
          renderInternal(
            surfaceParams,
            targetElementInfo,
            surfaceLaunchedEvent,
            onUpdated,
            closeSurface
          ),
        primaryButtonAction:
          surfaceParams.primaryButtonText &&
          (() => {
            handleClickAction(
              log.reserve(0x2238c092 /* tag_8omcs */),
              'primary',
              targetElementInfo?.selector,
              surfaceParams.surfaceType,
              surfaceParams.primaryButtonAction,
              surfaceParams.primaryButtonActionParams
            )
            closeSurface('primary')
          }),
        secondaryButtonAction:
          surfaceParams.secondaryButtonText &&
          (() => {
            handleClickAction(
              log.reserve(0x2238c093 /* tag_8omct */),
              'secondary',
              targetElementInfo?.selector,
              surfaceParams.surfaceType,
              surfaceParams.secondaryButtonAction,
              surfaceParams.secondaryButtonActionParams
            )
            closeSurface('secondary')
          }),
      },
    ])

    log.info(0x2361a7db /* tag_9y051 */, 'host render succeeded')
    surfaceLaunchedEvent?.()
  } catch (error) {
    log.error(
      0x2361a7dc /* tag_9y052 */,
      'error with host rendering, falling back to internal renderer',
      error
    )

    await renderInternal(
      surfaceParams,
      targetElementInfo,
      surfaceLaunchedEvent,
      onUpdated,
      onClosed
    )
  }
}
